/**
 * Diagnose & Audit – Admin-Sammelseite mit 6 Tabs:
 *   1. Konsistenz       – Konsistenz-Check (Datenintegritaet)
 *   2. Zeit-Health      – Zeiterfassung Health-Indikatoren
 *   3. Soll-Stunden     – Soll-Stunden Audit fuer Lohnbuero (time_tracking:view)
 *   4. Berechtigungen   – Permissions-Audit + Auto-Heal
 *   5. API-Drift        – Static-Analysis-Audit fuer Frontend↔Backend-Routen
 *   6. Benachrichtigung – Notification-Matrix (welche Events triggern welchen Kanal)
 *
 * Aktiver Tab via ?tab=…, alte Routen leiten weiter.
 */
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Wrench, Clock, ClipboardCheck, ShieldCheck, ShieldAlert, BellRing, Activity,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuditStatus from './AuditStatus';
import ConsistencyCheck from './ConsistencyCheck';
import TimeTrackingHealth from './TimeTrackingHealth';
import WorkingHoursAudit from './WorkingHoursAudit';
import PermissionsAudit from './PermissionsAudit';
import ApiAudit from './ApiAudit';
import NotificationMatrix from './NotificationMatrix';

const KEYS = {
  STATUS: 'status',
  CONSISTENCY: 'konsistenz',
  TIME_HEALTH: 'zeit-health',
  WORKING_HOURS: 'soll-stunden',
  PERMISSIONS: 'berechtigungen',
  API: 'api-drift',
  NOTIFICATIONS: 'benachrichtigung',
};

export default function AdminAuditHub({ embedded = false }) {
  const { isAdmin, hasPermission } = useAuth();
  const canViewTimeTracking = hasPermission('time_tracking', 'view');
  const canManageTimeTracking = hasPermission('time_tracking', 'edit');
  const canSettings = isAdmin || hasPermission('settings', 'view');

  const [searchParams, setSearchParams] = useSearchParams();

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (canSettings) tabs.push({ key: KEYS.STATUS, label: 'Status', icon: Activity, testid: 'tab-status' });
    if (canSettings) tabs.push({ key: KEYS.CONSISTENCY, label: 'Konsistenz', icon: Wrench, testid: 'tab-konsistenz' });
    if (isAdmin || canManageTimeTracking) tabs.push({ key: KEYS.TIME_HEALTH, label: 'Zeit-Health', icon: Clock, testid: 'tab-zeit-health' });
    if (isAdmin || canViewTimeTracking) {
      tabs.push({ key: KEYS.WORKING_HOURS, label: 'Soll-Stunden', icon: ClipboardCheck, testid: 'tab-soll-stunden' });
    }
    if (canSettings) tabs.push({ key: KEYS.PERMISSIONS, label: 'Berechtigungen', icon: ShieldCheck, testid: 'tab-berechtigungen' });
    if (canSettings) tabs.push({ key: KEYS.API, label: 'API-Drift', icon: ShieldAlert, testid: 'tab-api-drift' });
    if (canSettings) tabs.push({ key: KEYS.NOTIFICATIONS, label: 'Benachrichtigung', icon: BellRing, testid: 'tab-benachrichtigung' });
    return tabs;
  }, [isAdmin, canSettings, canViewTimeTracking, canManageTimeTracking]);

  const activeTab = useMemo(() => {
    const requested = searchParams.get('tab');
    if (requested && availableTabs.some((t) => t.key === requested)) return requested;
    return availableTabs[0]?.key || KEYS.WORKING_HOURS;
  }, [searchParams, availableTabs]);

  const handleTabChange = (val) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', val);
    setSearchParams(next, { replace: true });
  };

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-4 animate-fade-in" data-testid="admin-audit-hub-page">
        <div>
          <h1 className="font-heading font-bold text-xl sm:text-3xl text-foreground">Diagnose & Audit</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Datenintegrität, Zeiterfassung, Berechtigungen, API-Drift und Benachrichtigungen — alle Diagnose-Werkzeuge an einem Ort.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-3 sm:flex sm:flex-wrap sm:w-auto">
            {availableTabs.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={t.testid} className="flex items-center gap-2">
                <t.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {canSettings && (
            <TabsContent value={KEYS.STATUS} className="mt-4">
              <AuditStatus embedded />
            </TabsContent>
          )}
          {canSettings && (
            <TabsContent value={KEYS.CONSISTENCY} className="mt-4">
              <ConsistencyCheck embedded />
            </TabsContent>
          )}
          {(isAdmin || canManageTimeTracking) && (
            <TabsContent value={KEYS.TIME_HEALTH} className="mt-4">
              <TimeTrackingHealth embedded />
            </TabsContent>
          )}
          {(isAdmin || canViewTimeTracking) && (
            <TabsContent value={KEYS.WORKING_HOURS} className="mt-4">
              <WorkingHoursAudit embedded />
            </TabsContent>
          )}
          {canSettings && (
            <TabsContent value={KEYS.PERMISSIONS} className="mt-4">
              <PermissionsAudit embedded />
            </TabsContent>
          )}
          {canSettings && (
            <TabsContent value={KEYS.API} className="mt-4">
              <ApiAudit embedded />
            </TabsContent>
          )}
          {canSettings && (
            <TabsContent value={KEYS.NOTIFICATIONS} className="mt-4">
              <NotificationMatrix embedded />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </MaybeLayout>
  );
}
